'use client';

interface GentrificationData {
  index: number;
  stage: 'early-signs' | 'emerging' | 'mid-wave' | 'established';
  signals: {
    price_momentum: number;
    volume_change: number;
    new_build_ratio: number;
    leasehold_ratio: number;
  };
  transaction_count: number;
}

interface Props {
  data: GentrificationData | null;
  district: string;
}

const STAGE_META: Record<
  string,
  { label: string; color: string; bg: string; desc: string }
> = {
  'early-signs': {
    label: 'Early Signs',
    color: '#2d7a5f',
    bg: 'bg-[#d4ede5] text-[#1a4a3a]',
    desc: 'Early indicators of change — potential long-term opportunity',
  },
  emerging: {
    label: 'Emerging',
    color: '#eab308',
    bg: 'bg-yellow-50 text-yellow-700',
    desc: 'Accelerating momentum — area gaining attention from buyers and developers',
  },
  'mid-wave': {
    label: 'Mid-Wave',
    color: '#f97316',
    bg: 'bg-orange-50 text-orange-700',
    desc: 'Well underway — prices rising, significant new development activity',
  },
  established: {
    label: 'Established',
    color: '#1a4a3a',
    bg: 'bg-[#d4ede5] text-[#1a4a3a]',
    desc: 'Mature transformation — area is now considered prime or desirable',
  },
};

const SIGNAL_LABELS: Record<string, string> = {
  price_momentum: 'Price Momentum',
  volume_change: 'Volume Change',
  new_build_ratio: 'New Build Activity',
  leasehold_ratio: 'Leasehold Ratio',
};

// ── Semicircle Gauge (conic-gradient, pure CSS) ──────────────────────────────

function ArcGauge({ value, color }: { value: number; color: string }) {
  const pct = Math.min(Math.max(value, 0), 100);
  // Map 0–100 → 0–0.5 turns (half circle)
  const fillTurn = (pct / 100) * 0.5;

  return (
    <div className="flex flex-col items-center">
      {/* Outer wrapper clips to top half only */}
      <div style={{ width: 160, height: 80, overflow: 'hidden' }}>
        {/* Full circle with conic gradient, only top half visible */}
        <div
          style={{
            width: 160,
            height: 160,
            borderRadius: '50%',
            background: `conic-gradient(
              from 0.75turn,
              ${color} ${fillTurn}turn,
              #e8e4dc ${fillTurn}turn ${0.5}turn,
              transparent ${0.5}turn
            )`,
            // Mask out the center to create a ring (donut)
            WebkitMask: 'radial-gradient(circle at center, transparent 53px, black 54px)',
            mask: 'radial-gradient(circle at center, transparent 53px, black 54px)',
          }}
        />
      </div>
      {/* Score text — pulled up to overlap the gauge center */}
      <div className="-mt-10 text-center">
        <p className="text-2xl font-extrabold text-[#0f0f0d]">
          {value.toFixed(0)}
        </p>
        <p className="text-[10px] font-medium text-[#7a7870]">/ 100</p>
      </div>
    </div>
  );
}

// ── Signal Bar ───────────────────────────────────────────────────────────────

function SignalBar({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  // Normalize to 0-100 range for display (values can be negative or >100)
  const displayWidth = Math.min(Math.max(Math.abs(value), 0), 100);
  const isNegative = value < 0;

  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between">
        <span className="text-[11px] font-medium text-[#3a3935]">{label}</span>
        <span
          className={`text-[11px] font-semibold ${isNegative ? 'text-red-500' : 'text-[#0f0f0d]'}`}
        >
          {isNegative ? '' : '+'}
          {value.toFixed(1)}%
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-[#f3f0ea]">
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${displayWidth}%`,
            backgroundColor: isNegative ? '#ef4444' : color,
          }}
        />
      </div>
    </div>
  );
}

// ── Component ────────────────────────────────────────────────────────────────

export default function GentrificationGauge({ data, district }: Props) {
  if (!data) {
    return (
      <div className="rounded-xl border border-black/10 bg-[#faf8f4] p-4 text-center">
        <p className="text-sm text-[#7a7870]">
          Insufficient data for gentrification analysis in {district}
        </p>
      </div>
    );
  }

  const meta = STAGE_META[data.stage] ?? STAGE_META['early-signs'];

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between">
        <h4 className="text-sm font-semibold text-[#3a3935]">
          Gentrification Index
        </h4>
        <span
          className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${meta.bg}`}
        >
          {meta.label}
        </span>
      </div>

      <ArcGauge value={data.index} color={meta.color} />

      <p className="text-center text-xs text-[#7a7870]">{meta.desc}</p>

      {/* Signal breakdown */}
      <div className="space-y-2 pt-1">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-[#7a7870]">
          Signal Breakdown
        </p>
        {(Object.entries(data.signals) as [string, number][]).map(
          ([key, value]) => (
            <SignalBar
              key={key}
              label={SIGNAL_LABELS[key] ?? key}
              value={value}
              color={meta.color}
            />
          ),
        )}
      </div>

      <p className="text-[11px] text-[#7a7870]">
        Based on {data.transaction_count.toLocaleString()} transactions (last 3
        years)
      </p>
    </div>
  );
}
